import React from 'react';
import { Link, NavLink } from 'react-router-dom';
import { Sparkles, Sun, Moon, Monitor } from 'lucide-react';
import { useTheme } from '../theme';

const NAV_LINKS = [
  { to: '/', label: 'Converter' },
  { to: '/base64-encode', label: 'Encode' },
  { to: '/base64-decode', label: 'Decode' },
  { to: '/url-safe-base64', label: 'URL-Safe' },
  { to: '/what-is-base64', label: 'Guide' },
];

const FOOTER_GROUPS: { title: string; links: { to: string; label: string }[] }[] = [
  {
    title: 'Tools',
    links: [
      { to: '/', label: 'Encoder / Decoder' },
      { to: '/url-safe-base64', label: 'URL-Safe Base64' },
      { to: '/image-to-base64', label: 'Image to Base64' },
      { to: '/base64-to-image', label: 'Base64 to Image' },
      { to: '/json-to-base64', label: 'JSON to Base64' },
      { to: '/hex-to-base64', label: 'Hex to Base64' },
      { to: '/xml-to-base64', label: 'XML to Base64' },
      { to: '/csv-to-base64', label: 'CSV to Base64' },
      { to: '/yaml-to-base64', label: 'YAML to Base64' },
      { to: '/svg-to-base64', label: 'SVG to Base64' },
      { to: '/binary-to-base64', label: 'Binary to Base64' },
      { to: '/base64-size-calculator', label: 'Size Calculator' },
      { to: '/base64-validator', label: 'Base64 Validator' },
      { to: '/base64-mime-encode', label: 'MIME Base64 Encoder' },
    ],
  },
  {
    title: 'Code examples',
    links: [
      { to: '/base64-encode-javascript', label: 'Base64 in JavaScript' },
      { to: '/base64-encode-python', label: 'Base64 in Python' },
      { to: '/base64-encode-java', label: 'Base64 in Java' },
      { to: '/base64-encode-php', label: 'Base64 in PHP' },
      { to: '/base64-encode-csharp', label: 'Base64 in C#' },
      { to: '/base64-encode-go', label: 'Base64 in Go' },
      { to: '/base64-encode-ruby', label: 'Base64 in Ruby' },
      { to: '/base64-encode-rust', label: 'Base64 in Rust' },
      { to: '/base64-encode-swift', label: 'Base64 in Swift' },
      { to: '/base64-encode-kotlin', label: 'Base64 in Kotlin' },
      { to: '/base64-encode-cpp', label: 'Base64 in C++' },
      { to: '/base64-encode-dart', label: 'Base64 in Dart' },
      { to: '/base64-encode-bash', label: 'Base64 in Bash' },
      { to: '/base64-encode-sql', label: 'Base64 in SQL' },
    ],
  },
  {
    title: 'Learn',
    links: [
      { to: '/what-is-base64', label: 'What is Base64?' },
      { to: '/base64-encode', label: 'Base64 Encode' },
      { to: '/base64-decode', label: 'Base64 Decode' },
      { to: '/base64-vs-hex-vs-base32', label: 'Base64 vs Hex vs Base32' },
      { to: '/is-base64-encryption', label: 'Is Base64 Encryption?' },
      { to: '/jwt-base64url-encoding', label: 'JWT & Base64URL' },
      { to: '/base64-data-uri-guide', label: 'Base64 Data URIs' },
      { to: '/base64-file-size-overhead', label: 'Why Base64 Is ~33% Larger' },
      { to: '/base64-encoding-errors', label: 'Common Base64 Errors' },
      { to: '/base64-for-email-attachments', label: 'Base64 for Email' },
      { to: '/base64-database-storage', label: 'Base64 in Databases' },
    ],
  },
];

export default function Layout({ children }: { children: React.ReactNode }) {
  const { darkMode, themeSource, toggleTheme, useSystemTheme, themeClasses } = useTheme();

  return (
    <div className={`min-h-screen ${themeClasses.bg} transition-colors duration-300`}>
      {/* Header */}
      <header className={`sticky top-0 z-10 ${themeClasses.headerBg} border-b`}>
        <div className="max-w-6xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <Link to="/" className="inline-flex items-center gap-2">
              <div className="p-2 bg-indigo-600 rounded-lg">
                <Sparkles className="w-6 h-6 text-white" />
              </div>
              <span className={`text-lg font-bold ${themeClasses.accentText}`}>
                EasyBase64
              </span>
            </Link>

            <nav aria-label="Primary" className="order-last w-full md:order-none md:w-auto flex items-center gap-1 overflow-x-auto md:overflow-visible md:flex-wrap justify-start md:justify-center -mx-4 px-4 md:mx-0 md:px-0">
              {NAV_LINKS.map((link) => (
                <NavLink
                  key={link.to}
                  to={link.to}
                  end={link.to === '/'}
                  className={({ isActive }) =>
                    `px-3 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap shrink-0 ${
                      isActive
                        ? themeClasses.accent
                        : `${themeClasses.textSecondary} ${themeClasses.buttonSecondary} border`
                    }`}
                >
                  {link.label}
                </NavLink>
              ))}
            </nav>

            <div className="flex items-center gap-2">
              <button
                onClick={useSystemTheme}
                className={`p-2 rounded-xl ${themeClasses.buttonSecondary} border transition-colors ${themeSource === 'system' ? 'ring-2 ring-indigo-500' : ''}`}
                aria-label="Use system theme"
                title="Use system theme"
              >
                <Monitor className="w-5 h-5" />
              </button>
              <button
                onClick={toggleTheme}
                className={`p-2 rounded-xl ${themeClasses.buttonSecondary} border transition-colors ${themeSource === 'manual' ? 'ring-2 ring-indigo-500' : ''}`}
                aria-label="Toggle light or dark theme"
                title={darkMode ? 'Switch to light mode' : 'Switch to dark mode'}
              >
                {darkMode ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Page content */}
      <main className="relative z-10 max-w-6xl mx-auto px-4 py-12">
        {children}
      </main>

      {/* Footer */}
      <footer className={`relative z-10 ${themeClasses.footerBg} border-t py-12 mt-16`}>
        <div className="max-w-6xl mx-auto px-4">
          <div className="grid gap-8 md:grid-cols-4">
            <div>
              <div className="inline-flex items-center gap-2 mb-4">
                <div className="p-2 bg-indigo-600 rounded-lg">
                  <Sparkles className="w-5 h-5 text-white" />
                </div>
                <span className={`text-xl font-bold ${themeClasses.accentText}`}>
                  EasyBase64
                </span>
              </div>
              <p className={`${themeClasses.textMuted} max-w-md`}>
                A free, fast and private online Base64 encoder and decoder. All conversion happens
                locally in your browser — your data never leaves your device.
              </p>
            </div>
            {FOOTER_GROUPS.map((group) => (
              <nav key={group.title} aria-label={group.title} className="flex flex-col gap-2">
                <h2 className={`text-sm font-semibold ${themeClasses.textPrimary}`}>{group.title}</h2>
                {group.links.map((link) => (
                  <Link key={link.to + link.label} to={link.to} className={`text-sm ${themeClasses.textSecondary} hover:underline`}>
                    {link.label}
                  </Link>
                ))}
              </nav>
            ))}
          </div>
        </div>
      </footer>
    </div>
  );
}
